import { useParams } from 'react-router-dom';

import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { useGetFullClaimContextQuery } from '@savgroup-front-common/core/hooks/useGetFullClaimContextQuery';
import { useGetModelByIdQuery } from '@/view/app/hooks/useGetModelByIdQuery';
import { useGetSolutionTypeQuery } from '@/view/app/hooks/useGetSolutionTypeQuery';
import { useGetFileStatesQuery } from '@/view/app/hooks/Workflow/useGetFileStatesQuery';
import { buildFileFollowupUrl } from '@/view/app/NewLayout/ClassiqueLayout.helpers';
import { useLayout } from '@/view/app/NewLayout/NewLayout.hooks';

import { FINAL_ROUTES } from '../../app';

import {
  adaptCustomerFileFullSummary,
  adaptHistory,
  adaptProductsFromFileFollowupsPage,
} from './FileFollowupsPage.adapters';
import { useGetCustomerFileFullSummaryQuery } from './hooks/useGetCustomerFileFullSummaryQuery';
import messages from './messages';

export const useGetProductsDetails = () => {
  const { fileId } = useParams() as { fileId: string };
  const { fileInfo, isLoading: isLoadingFileSummary } =
    useGetCustomerFileFullSummaryQuery({ fileId });

  const productsInfoAdapted = adaptCustomerFileFullSummary(fileInfo);
  const { models, isLoading: isLoadingModel } = useGetModelByIdQuery({
    productsInfo: productsInfoAdapted,
  });
  const { solution, isLoading: isLoadingSolution } = useGetSolutionTypeQuery({
    solutionTypeId: fileInfo?.solutionId,
  });

  return {
    productsDetails: adaptProductsFromFileFollowupsPage({
      models,
      productsInfoAdapted,
      solution,
    }),
    fileId,
    isLoadingModel,
    isLoadingSolution,
    isLoadingFileSummary,
  };
};

export const useFileFollowupsPage = () => {
  const { fileId } = useParams() as { fileId: string };
  const { fileInfo, isLoading: isLoadingFileSummary } =
    useGetCustomerFileFullSummaryQuery({ fileId });

  const { histories, isLoading: isLoadingHistories } = useGetFileStatesQuery({
    fileId,
  });

  const claimId = fileInfo?.claimInfoSummary.claimId;

  const { fullClaim, isLoading: isLoadingFullClaim } =
    useGetFullClaimContextQuery({
      claimId,
    });

  return {
    histories: adaptHistory(histories),
    fullClaim,
    fileInfo,
    isLoadingFileSummary,
    isLoadingHistories,
    isLoadingFullClaim,
  };
};

export const useBreadCrumbs = ({
  isLoading,
  productsDetails,
  fileId,
}: {
  isLoading: boolean;
  productsDetails: ReturnType<typeof adaptProductsFromFileFollowupsPage>;
  fileId: string;
}) => {
  const { enableServicePortal } = useLayout();

  return [
    {
      label: messages.breadcrumbMyRequests,
      route: enableServicePortal ? FINAL_ROUTES.HOME : FINAL_ROUTES.PRODUCTS,
    },
    {
      label: productsDetails
        ? safeFormattedIntlString(messages.bannerTitle, {
            reference: isLoading ? '...' : productsDetails[0]?.fileReference,
          })
        : messages.bannerTitleUnknown,
      route: buildFileFollowupUrl({ fileId }),
    },
  ];
};
